import { PageShell, GetStarted } from '../../components';
import { PageBody, PageHero, Section, FeatureRows, DataTable } from '../../components/page';

/**
 * Comparison: Statio against running MCP servers directly. Claims are checked
 * against .impeccable/product-truth.md; positioning follows Pugio's
 * docs/POSITIONING.md (lead with the per-tool problem, never "platform").
 */

const PROBLEM = [
  {
    title: 'A read tool and a destructive tool, on one server',
    body: 'A database MCP server can hold a tool that reads rows next to one that deletes them. If access is set per server, an agent gets both or neither.',
  },
  {
    title: 'Statio decides per tool',
    body: 'Grant a member or an agent access to the server, then deny the tools they should not call. A tool-level deny overrides the server grant, and the gateway checks it on every call before the credential is used.',
  },
];

const ROWS = [
  ['Authentication', 'Build your own', 'JWT gateway with auto-refresh'],
  ['Credential storage', 'Env vars or config files', 'Encrypted at rest (AES-256-GCM)'],
  ['Access policies', 'None', 'Per-tool, checked on every call'],
  ['Audit trail', 'None unless you build it', 'Every call logged automatically'],
  ['Rate limiting', 'None', 'Per organization, by plan'],
  ['Multi-tenancy', 'Build from scratch', 'Included with tenant isolation'],
  ['Token lifetime', 'Manual', 'Access and agent tokens 5 min, refresh 7 days'],
];

const List = ({ items }: { items: string[] }) => (
  <>
    {items.map((t) => (
      <p key={t}>{t}</p>
    ))}
  </>
);

const FIT = [
  {
    title: 'When to use raw MCP',
    body: (
      <List
        items={[
          'Single agent calling one or two APIs in a personal project.',
          'No customer data involved, just your own accounts.',
          'No compliance or audit requirements.',
          'You want to learn how MCP works at the protocol level.',
        ]}
      />
    ),
  },
  {
    title: 'When to choose Statio',
    body: (
      <List
        items={[
          'Multiple agents calling multiple APIs in production.',
          'Customer data flows through your agents. You need tenant isolation.',
          'Compliance requires audit trails of every API call your agents make.',
          'You need access policies so agents only reach the tools they should.',
          'API keys are scattered across agent configs and you want them stored encrypted in one place.',
        ]}
      />
    ),
  },
];

export default function StatioVsRawMcp() {
  return (
    <PageShell>
      <PageBody>
        <PageHero
          title="Statio compared with using MCP directly"
          lede="MCP specifies how an agent and a tool talk to each other. It says nothing about who is allowed to call what, where the credential lives, or what gets written down afterwards. Those are yours to build, or ours."
          art="aqueduct"
        />
        <Section id="problem" title="Access set per server is all or nothing" split>
          <FeatureRows items={PROBLEM} columns={1} />
        </Section>
        <Section id="compare" title="Side by side">
          <DataTable
            caption="Statio compared with raw MCP"
            columns={['', 'Raw MCP', 'Statio']}
            rows={ROWS}
            highlight={2}
          />
        </Section>
        <Section id="fit" title="Which fits">
          <FeatureRows items={FIT} />
        </Section>
      </PageBody>
      <GetStarted />
    </PageShell>
  );
}
